import { Box, Grid } from "@mui/material";
import { useMemo } from "react";
import {
  IconHeart,
  IconEye,
  IconBrandWhatsapp,
  IconPhoneRinging,
  IconMail,
  IconBuildingBank,
  IconInfoCircle
} from "@tabler/icons-react";
import MxDetailSmallCard from "@rf-l1/page/details/MxDetailCardSmall";
import MxTypography from "@rf-l1/MxTypography";
import MxIcons from "@rf-l1/MxIcons";
import MxTooltipContent from "@rf-l1/tooltip/MxTooltipContent";
import { usePropertyDetailPage } from "../../context/PropertyDetailPageContext";
import { C_BUSINESS_TYPE } from "@/src-refactor/modules/shared/model/const/property/business-type.const";
import RemaxLogo from "@icons-rf/remax-logo.svg?react";

/**
 * PropertyStatisticsCard
 *
 * Tarjeta que muestra las estadísticas de la propiedad:
 * - Me gusta
 * - Vistas
 * - Clics en WhatsApp
 * - Clics en llamada
 * - Correos enviados
 * - Referimientos hipotecarios (solo para propiedades en venta)
 */
const PropertyStatisticsCard = () => {
  const propertyData = usePropertyDetailPage();
  const { statistics, businessType } = propertyData;

  if (!statistics) {
    return null;
  }

  const hasLeadCounts =
    statistics.leadCounts.whatsapp >= 0 ||
    statistics.leadCounts.call >= 0 ||
    statistics.leadCounts.mail >= 0 ||
    statistics.leadCounts.mortgageReferral >= 0;

  const isForSale = businessType !== C_BUSINESS_TYPE.SELL.name;

  const stats = useMemo(
    () => [
      {
        show: true,
        icon: <IconHeart size={24} stroke={1.5} color="#DC1C2E" />,
        label: "Me gusta",
        value: statistics.likes,
        infoText: "Cantidad de veces que los usuarios han dado like a la propiedad."
      },
      {
        show: true,
        icon: <IconEye size={24} stroke={1.5} color="#1A3668" />,
        label: "Vistas",
        value: statistics.views,
        infoText:
          "Número total de veces que los usuarios han visualizado la propiedad."
      },
      {
        show: hasLeadCounts,
        icon: <IconBrandWhatsapp size={24} stroke={1.5} color="#43EB61" />,
        label: "Clic en botón de Whatsapp",
        value: statistics.leadCounts.whatsapp,
        infoText:
          "Cantidad de veces que los usuarios han demostrado la intención de contactar por Whatsapp haciendo clic sobre el botón."
      },
      {
        show: hasLeadCounts,
        icon: <IconPhoneRinging size={24} stroke={1.5} color="#535354" />,
        label: "Clic en botón de llamada",
        value: statistics.leadCounts.call,
        infoText:
          "Número de veces que los usuarios han presionado el botón de llamar."
      },
      {
        show: hasLeadCounts,
        icon: <IconMail size={24} stroke={1.5} color="#43B7FA" />,
        label: "Correos enviados por usuarios",
        value: statistics.leadCounts.mail,
        infoText:
          "Cantidad de correos enviados por los usuarios desde el botón de enviar correo."
      },
      {
        show: hasLeadCounts && isForSale,
        icon: <IconBuildingBank size={20} stroke={1.5} color="#393939" />,
        label: "Referimientos hipotecarios solicitados",
        value: statistics.leadCounts.mortgageReferral,
        infoText:
          "Cantidad de veces que los usuarios han solicitado un referimiento hipotecario."
      }
    ],
    [statistics, hasLeadCounts, isForSale]
  );

  const visibleStats = stats.filter(stat => stat.show);

  if (visibleStats.length === 0) {
    return null;
  }

  return (
    <MxDetailSmallCard title="Estadísticas">
      <Box
        sx={{
          padding: 2,
          border: "1px solid #ddd",
          borderRadius: 2,
          width: "100%"
        }}
      >
        <Grid container spacing={0}>
          {visibleStats.map((stat, index) => (
            <Grid
              item
              xs={12}
              key={index}
              sx={{
                display: "flex",
                alignItems: "center",
                padding: "8px 0",
                borderBottom:
                  index !== visibleStats.length - 1 ? "1px solid #e0e0e0" : "none",
                "& svg": {
                  display: "block",
                  width: "24px",
                  height: "24px"
                }
              }}
            >
              <Box
                sx={{
                  marginRight: 2,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center"
                }}
              >
                {stat.icon}
              </Box>

              <Grid container justifyContent="space-between">
                <Grid item display="flex" gap={1} alignItems="center">
                  <MxTypography variant="caption" color="grey.700" fontWeight={400}>
                    {stat.label}
                  </MxTypography>

                  <MxTooltipContent
                    template={
                      <Grid>
                        <MxTypography
                          variant="overline"
                          color="grey.700"
                          fontWeight={500}
                        >
                          {stat.infoText}
                        </MxTypography>
                      </Grid>
                    }
                  >
                    <MxIcons color="primary.main">
                      <IconInfoCircle style={{ width: "14px", height: "14px" }} />
                    </MxIcons>
                  </MxTooltipContent>
                </Grid>

                <Grid item mr={2}>
                  <MxTypography
                    variant="caption"
                    color="primary.main"
                    fontWeight={400}
                  >
                    {stat.value}
                  </MxTypography>
                </Grid>
              </Grid>
            </Grid>
          ))}
        </Grid>
      </Box>

      <Grid
        container
        alignItems="center"
        justifyContent="flex-end"
        spacing={1}
        marginTop={1}
      >
        <Grid item>
          <MxIcons size={14}>
            <RemaxLogo />
          </MxIcons>
        </Grid>

        <Grid item xs="auto">
          <MxTypography
            variant="caption"
            color="grey.600"
            fontWeight={500}
            fontSize={10}
          >
            FUENTE REMAXRD.COM
          </MxTypography>
        </Grid>
      </Grid>
    </MxDetailSmallCard>
  );
};

export default PropertyStatisticsCard;
